/**
 * ContextMenu
 *
 * Unified context menu component for the application.
 * Wraps Ant Design Dropdown with right-click trigger and event protection.
 *
 * Features:
 * - Coordinates with ContextMenuProvider (only one menu open at a time)
 * - Prevents right-click from propagating to child interactive controls
 * - Consistent styling and behavior across all context menus
 */

import { FC, MouseEventHandler, ReactNode } from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { useContextMenu } from './useContextMenu';
import { ContextMenuWrapper } from './ContextMenuWrapper';

type ContextMenuProps = {
  /** Unique identifier for this menu instance */
  menuId: string;
  /** Menu items configuration */
  items: MenuProps['items'];
  /** Whether the context menu is disabled */
  disabled?: boolean;
  /** Runs on every open/close, alongside the provider's own coordination */
  onOpenChange?: (open: boolean) => void;
  /**
   * Right-click on the trigger, in the capture phase — before the menu opens and
   * before any inner wrapper's bubble-phase `stopPropagation` can hide the event.
   * `FormContextMenu` records which field the pointer is over with it. Forwarded
   * explicitly rather than by widening this component to arbitrary DOM props.
   */
  onContextMenuCapture?: MouseEventHandler<HTMLDivElement>;
  /** Content that triggers the context menu on right-click */
  children: ReactNode;
};

const ContextMenu: FC<ContextMenuProps> = ({
  menuId,
  items,
  disabled = false,
  onOpenChange,
  onContextMenuCapture,
  children,
}) => {
  const { isControlled, isOpen, onOpenChange: coordinateOpenChange } = useContextMenu(menuId);

  const handleOpenChange = (open: boolean) => {
    coordinateOpenChange?.(open);
    onOpenChange?.(open);
  };

  const controlledProps = isControlled ? { open: isOpen } : {};

  return (
    <Dropdown
      menu={{ items }}
      trigger={['contextMenu']}
      disabled={disabled}
      onOpenChange={handleOpenChange}
      {...controlledProps}
    >
      <ContextMenuWrapper onContextMenuCapture={onContextMenuCapture}>
        {children}
      </ContextMenuWrapper>
    </Dropdown>
  );
};

export { ContextMenu };
export type { ContextMenuProps };

